// Re-login modal bus — mirrors the "no parent state, DOM-event controlled" pattern
// used by saveSuccessModalBus, footnoteModalBus, etc.
//
// ONLY opened by intentional write-time recovery (EditFilings Save) when the
// refresh token is truly dead. AuthContext must NOT open this on every
// SIGNED_OUT — that caused surprise "Session Expired" popups during navigation
// / slow page loads from refresh-token races.
//
// EditFilings must NOT hold modal-visibility state (a re-render of the 12k+ input
// tree can destroy unsaved DOM values). The modal is always mounted and toggled
// via document events; context is stashed here before the open event fires.

export type ReloginModalContext = {
  /** Email of the currently signed-in user (prefilled, read-only). */
  email: string;
  /** Called after a successful re-login, e.g. to retry the save. */
  onSuccess: () => void;
  /**
   * Called when the user dismisses the modal without signing in.
   * Optional — page-level flows (EditFilings save) just stay on the page;
   * the app-wide flow uses this to fall back to a normal sign-out redirect.
   */
  onCancel?: () => void;
  /**
   * Label for the dismiss button. Defaults to "Cancel" (save-time flow, where
   * dismissing just closes the dialog). The app-wide flow sets "Go to Login"
   * because dismissing there redirects to the sign-in page.
   */
  cancelLabel?: string;
};

let currentContext: ReloginModalContext | null = null;

// ── Re-login guard ──────────────────────────────────────────────────────────
// While active, AuthContext suppresses its SIGNED_OUT redirect (window.location
// = '/signin') and skips state updates on SIGNED_IN/TOKEN_REFRESHED. This makes
// the re-login a pure background token continuation: no navigation (which would
// fire the browser leave-page dialog and lose the form) and no app re-render.
//
// Set when in-place session recovery starts (EditFilings save), cleared when
// the save finishes, fails for non-auth reasons, or the user cancels the modal.
let reloginGuardActive = false;

export function setReloginGuard(active: boolean): void {
  reloginGuardActive = active;
}

export function isReloginGuardActive(): boolean {
  return reloginGuardActive;
}

export const RELOGIN_MODAL_OPEN_EVENT = 'reloginModal:open';
export const RELOGIN_MODAL_CLOSE_EVENT = 'reloginModal:close';

export function setReloginModalContext(ctx: ReloginModalContext): void {
  currentContext = ctx;
}

export function getReloginModalContext(): ReloginModalContext | null {
  return currentContext;
}

export function openReloginModal(): void {
  document.dispatchEvent(new CustomEvent(RELOGIN_MODAL_OPEN_EVENT));
}

export function closeReloginModal(): void {
  document.dispatchEvent(new CustomEvent(RELOGIN_MODAL_CLOSE_EVENT));
}
